'use client'

import {useCallback, useEffect, useState} from 'react'
import {useSession} from '~/auth/AuthProvider'
import useSnackbar from '~/components/snackbar/useSnackbar'
import {AssociatedPoFTopic, associatePoFTopicWithOrganisation, deletePoFTopicAssociationWithOrganisation, getPoFTopicsAssociatedWithOrganisation, getPoFTopicTreesForOrganisation, getSoftwareQualityEvaluationDataForOrganisation, SoftwareEvaluationDataForReporting} from './apiOrganisationSoftwareQuality'
import {DownloadFormat, downloadQualityReportForOrganisation, downloadSavedQualityReport, getSavedQualityReportsForOrganisation, SavedQualityReport} from '~/components/organisation/reporting/apiOrganisationSoftwareQuality'
import {PoFTopicNode, SoftwareEvaluationReportingInformation} from '~/components/software/indicator/apiSoftwareIndicator'

export function useSoftwareQualityOverview(organisation?: string, year?: string) {
  const {token, user} = useSession()
  const {showErrorMessage} = useSnackbar()
  const [loading, setLoading] = useState(true)

  const [savedQualityReports, setSavedQualityReports] = useState<SavedQualityReport[]>([])
  const [evaluatedSoftware, setEvaluatedSoftware] = useState<SoftwareEvaluationDataForReporting[]>([])
  const [pofTopicTrees, setPofTopicTrees] = useState<PoFTopicNode[]>([])
  const [associatedPofTopics, setAssociatedPofTopics] = useState<SoftwareEvaluationReportingInformation['pof_topic'][]>([])

  useEffect(() => {
    getEvaluatedSoftware()
    getSavedQualityReports()
    getPoFTopicTrees()
    getAssociatedPoFTopicsForOrganisation()
  }, [organisation, year])

  const getEvaluatedSoftware = useCallback(async () => {
    setLoading(true)
    if (organisation && year) {
      const softwareEntries = await getSoftwareQualityEvaluationDataForOrganisation({organisation, year, token})
      setEvaluatedSoftware(softwareEntries)
    }
    setLoading(false)
  }, [token, organisation, year])

  const getSavedQualityReports = useCallback(async () => {
    setLoading(true)
    if (organisation && year) {
      const reports = await getSavedQualityReportsForOrganisation({organisation, year, token})
      setSavedQualityReports(reports)
    }
    setLoading(false)
  }, [organisation, token, year])


  const transformBlobForDownload = useCallback(async (format: DownloadFormat, blob: Blob, reportDateIso: string) => {

    // Swedish locale seems to do the right thing here: YYYY-MM-DD hh:mm:ss+hh:mm GMT+hh:mm
    const formatDate = (date: Date) => date.toLocaleString('sv', {timeZoneName: 'longOffset'}).replace(' ', 'T').replace(' GMT', '')

    const downloadDate = formatDate(new Date())
    const reportDate = formatDate(new Date(reportDateIso))
    const link = document.createElement('a')
    link.target = '_blank'

    if (format === 'json') {
      link.download = `evaluation-report-${year}-${reportDate}.json`
      const entries = await new Response(blob).json()
      const payload = JSON.stringify({
        'reporting_period': year,
        'report_date': formatDate(new Date(reportDate)),
        'download_date': downloadDate,
        'software_entries': entries
      }, undefined, 2)
      link.href = URL.createObjectURL(new Blob([payload], {type: 'application/json'}))
    } else {
      link.download = `evaluation-report-${year}-${reportDate}.csv`
      link.href = URL.createObjectURL(blob)
    }

    link.click()
  }, [year])

  const downloadQualityReports = useCallback(async (format: DownloadFormat) => {
    if (organisation && year) {
      const blob = await downloadQualityReportForOrganisation({organisation, year, format, token})

      if (blob === null) {
        return
      }

      await transformBlobForDownload(format, blob, (new Date()).toISOString())
      await getSavedQualityReports()
    }
  }, [token, organisation, year])

  const downloadSavedQualityReports = useCallback(async (id: number, format: DownloadFormat) => {
    const blob = await downloadSavedQualityReport({id, format, token})
    const reportDate = savedQualityReports.find((report) => report.id === id)?.timestamp

    if (blob === null || reportDate === undefined) {
      return
    }

    await transformBlobForDownload(format, blob, reportDate)
  }, [token, savedQualityReports])

  const getPoFTopicTrees = useCallback(async () => {
    setLoading(true)
    if (organisation) {
      const trees = await getPoFTopicTreesForOrganisation({organisation, token})
      setPofTopicTrees(trees)
    }
    setLoading(false)
  }, [token, organisation])

  const savePoFTopicForOrganisation = useCallback(async (topic_id: string) => {
    setLoading(true)
    console.log('in save topic for org func')
    if (organisation) {
      console.log('before awaiting pof for org')
      await associatePoFTopicWithOrganisation({organisation, topic: topic_id, token})
      getAssociatedPoFTopicsForOrganisation()
      getPoFTopicTrees()
    }
    setLoading(false)
  }, [organisation])

  const deletePoFTopicForOrganisation = useCallback(async (topic_id: string) => {
    setLoading(true)
    if (organisation) {
      await deletePoFTopicAssociationWithOrganisation({organisation, topic: topic_id, token})
      getAssociatedPoFTopicsForOrganisation()
      getPoFTopicTrees()
    }
    setLoading(false)
  }, [organisation])

  const getAssociatedPoFTopicsForOrganisation = useCallback(async () => {
    setLoading(true)
    if (organisation) {
      const topics: AssociatedPoFTopic[] = await getPoFTopicsAssociatedWithOrganisation({organisation, token})
      setAssociatedPofTopics(topics)
      getPoFTopicTrees()
    }
    setLoading(false)
  }, [token, organisation])


  return {
    loading,
    organisation,
    year,
    evaluatedSoftware,
    savedQualityReports,
    downloadQualityReports,
    downloadSavedQualityReports,
    pofTopicTrees,
    savePoFTopicForOrganisation,
    deletePoFTopicForOrganisation,
    associatedPofTopics
  }
}
